import type {
  AppointmentId,
  BarcodeGrantControlKind,
  IdempotencyKey,
  LocationId,
  SchedulingTimezone,
  StaffOperationsSnapshot,
  StaffOperationsView as StaffOperationsViewKind,
  StaffVisitSummary,
  VisitId,
  VisitPacketCodeFormat,
  VisitTransitionRequest,
} from "@whitehaven/contracts";
import { isStaffOperationsSnapshot } from "@whitehaven/contracts";
import {
  canAccessPortalArea,
  type AuthorizationSubject,
} from "@whitehaven/permissions";
import { ContentState } from "@whitehaven/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import type {
  StaffBarcodeControlGateway,
  StaffOperationsMutationGateway,
  StaffOperationsReadGateway,
  StaffPrintDialogAuditGateway,
  StaffVisitPacketBundle,
  StaffVisitPacketGateway,
} from "./staff-operations-gateway";
import {
  StaffOperationsView,
  type StaffOperationsFeedback,
} from "./StaffOperationsView";
import {
  createStaffOperationsProjectionKey,
  matchesStaffOperationsSnapshotQuery,
  staffOperationsCapabilityArea,
} from "./staff-operations-model";

interface StaffOperationsRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly view: StaffOperationsViewKind;
  readonly initialAppointmentId: AppointmentId | null;
  readonly initialVisitId: VisitId | null;
  readonly date: string;
  readonly locationTimezones: Readonly<
    Record<string, SchedulingTimezone | undefined>
  >;
  readonly readGateway: StaffOperationsReadGateway | null;
  readonly mutationGateway: StaffOperationsMutationGateway | null;
  readonly packetGateway: StaffVisitPacketGateway | null;
  readonly printDialogAuditGateway: StaffPrintDialogAuditGateway | null;
  readonly barcodeControlGateway: StaffBarcodeControlGateway | null;
  readonly printerProfileId: string | null;
  readonly syntheticPreview: boolean;
}

type LoadState =
  | { readonly kind: "loading"; readonly projectionKey: string | null }
  | { readonly kind: "error"; readonly projectionKey: string }
  | {
      readonly kind: "ready";
      readonly projectionKey: string;
      readonly snapshot: StaffOperationsSnapshot;
    };

function secureId(prefix: string): string | null {
  try {
    return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
  } catch {
    return null;
  }
}

export function StaffOperationsRoute({
  subject,
  view,
  initialAppointmentId,
  initialVisitId,
  date,
  locationTimezones,
  readGateway,
  mutationGateway,
  packetGateway,
  printDialogAuditGateway,
  barcodeControlGateway,
  printerProfileId,
  syntheticPreview,
}: StaffOperationsRouteProps) {
  const [locationId, setLocationId] = useState<string | null>(
    subject?.locationIds[0] ?? null,
  );
  const [selectedDate, setSelectedDate] = useState(date);
  const [appointmentId, setAppointmentId] = useState<AppointmentId | null>(
    initialAppointmentId,
  );
  const [visitId, setVisitId] = useState<VisitId | null>(initialVisitId);
  const locationTimezone =
    locationId !== null && Object.hasOwn(locationTimezones, locationId)
      ? (locationTimezones[locationId] ?? null)
      : null;
  const capabilityArea = staffOperationsCapabilityArea(view);
  const authorized =
    subject !== null &&
    locationId !== null &&
    locationTimezone !== null &&
    canAccessPortalArea(subject, capabilityArea, "view", locationId);
  const canMutate =
    subject !== null &&
    locationId !== null &&
    mutationGateway !== null &&
    canAccessPortalArea(subject, capabilityArea, "write", locationId);
  const canPrint =
    canMutate &&
    packetGateway !== null &&
    printDialogAuditGateway !== null &&
    printerProfileId !== null;
  const canManageCodes = canMutate && barcodeControlGateway !== null;
  const projectionKey = createStaffOperationsProjectionKey({
    userId: subject?.userId ?? null,
    locationId,
    locationTimezone,
    date: selectedDate,
    appointmentId,
    visitId,
    view,
  });
  const [loadState, setLoadState] = useState<LoadState>({
    kind: "loading",
    projectionKey: null,
  });
  const [pendingVisitId, setPendingVisitId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<StaffOperationsFeedback | null>(
    null,
  );
  const [printBundles, setPrintBundles] = useState<
    readonly StaffVisitPacketBundle[]
  >([]);
  const activeRequest = useRef<AbortController | null>(null);
  const sequence = useRef(0);
  const printFrame = useRef<number | null>(null);
  const printAuthorityKey = JSON.stringify([
    subject?.userId ?? null,
    authorized,
    canPrint,
    canManageCodes,
    projectionKey,
    printerProfileId,
  ]);
  const printAuthorityKeyRef = useRef(printAuthorityKey);
  const projectionKeyRef = useRef(projectionKey);
  const loadStateRef = useRef(loadState);
  printAuthorityKeyRef.current = printAuthorityKey;
  projectionKeyRef.current = projectionKey;
  loadStateRef.current = loadState;

  const hasCurrentPrintAuthority = (
    expectedAuthorityKey: string,
    generation: number,
  ) => {
    const current = loadStateRef.current;
    return (
      printAuthorityKeyRef.current === expectedAuthorityKey &&
      isProtectedStateGenerationCurrent(generation) &&
      current.kind === "ready" &&
      current.projectionKey === projectionKeyRef.current
    );
  };

  const load = useCallback(() => {
    if (
      !authorized ||
      !locationId ||
      !locationTimezone ||
      !readGateway ||
      activeRequest.current
    ) {
      return;
    }
    const requestSequence = sequence.current + 1;
    sequence.current = requestSequence;
    const controller = new AbortController();
    activeRequest.current = controller;
    const generation = captureProtectedStateGeneration();
    const requestProjectionKey = projectionKey;
    setLoadState({
      kind: "loading",
      projectionKey: requestProjectionKey,
    });
    setFeedback(null);
    setPrintBundles([]);
    void readGateway(
      {
        date: selectedDate,
        locationId: locationId as LocationId,
        locationTimezone,
        ...(appointmentId ? { appointmentId } : {}),
        ...(visitId ? { visitId } : {}),
        view,
      },
      { signal: controller.signal },
    )
      .then(
        (snapshot) => {
          if (
            controller.signal.aborted ||
            sequence.current !== requestSequence ||
            !isProtectedStateGenerationCurrent(generation)
          ) {
            return;
          }
          setLoadState(
            isStaffOperationsSnapshot(snapshot) &&
              matchesStaffOperationsSnapshotQuery(snapshot, {
                date: selectedDate,
                locationId,
                locationTimezone,
                appointmentId,
                visitId,
              })
              ? {
                  kind: "ready",
                  projectionKey: requestProjectionKey,
                  snapshot,
                }
              : {
                  kind: "error",
                  projectionKey: requestProjectionKey,
                },
          );
        },
        () => {
          if (
            !controller.signal.aborted &&
            sequence.current === requestSequence &&
            isProtectedStateGenerationCurrent(generation)
          ) {
            setLoadState({
              kind: "error",
              projectionKey: requestProjectionKey,
            });
          }
        },
      )
      .finally(() => {
        if (sequence.current === requestSequence) {
          activeRequest.current = null;
        }
      });
  }, [
    appointmentId,
    authorized,
    locationId,
    locationTimezone,
    projectionKey,
    readGateway,
    selectedDate,
    visitId,
    view,
  ]);

  const priorPrintAuthorityKey = useRef(printAuthorityKey);
  useEffect(() => {
    if (priorPrintAuthorityKey.current === printAuthorityKey) return;
    priorPrintAuthorityKey.current = printAuthorityKey;
    sequence.current += 1;
    activeRequest.current?.abort();
    activeRequest.current = null;
    if (printFrame.current !== null) {
      cancelAnimationFrame(printFrame.current);
      printFrame.current = null;
    }
    setPrintBundles([]);
    setPendingVisitId(null);
    setBusy(false);
    setFeedback(null);
  }, [printAuthorityKey]);

  useEffect(() => {
    const unregister = registerProtectedStateCleaner("staff-operations", () => {
      sequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      if (printFrame.current !== null) {
        cancelAnimationFrame(printFrame.current);
        printFrame.current = null;
      }
      setLoadState({ kind: "loading", projectionKey: null });
      setPendingVisitId(null);
      setBusy(false);
      setFeedback(null);
      setPrintBundles([]);
    });
    load();
    return () => {
      sequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      if (printFrame.current !== null) {
        cancelAnimationFrame(printFrame.current);
        printFrame.current = null;
      }
      unregister();
    };
  }, [load, subject?.userId]);

  useEffect(() => {
    const nextLocationId = subject?.locationIds[0] ?? null;
    setLocationId(nextLocationId);
    setSelectedDate(date);
    setAppointmentId(initialAppointmentId);
    setVisitId(initialVisitId);
  }, [
    date,
    initialAppointmentId,
    initialVisitId,
    subject?.locationIds,
    subject?.userId,
  ]);

  useEffect(() => {
    if (!feedback) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById("staff-operations-feedback")?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [feedback]);

  const transition = useCallback(
    (visit: StaffVisitSummary, kind: VisitTransitionRequest["kind"]) => {
      if (
        !canMutate ||
        !mutationGateway ||
        activeRequest.current ||
        loadState.kind !== "ready"
      ) {
        return;
      }
      const idempotencyKey = secureId("staff_visit");
      const correlationId = secureId("correlation");
      if (!idempotencyKey || !correlationId) return;
      const controller = new AbortController();
      activeRequest.current = controller;
      const requestSequence = sequence.current + 1;
      sequence.current = requestSequence;
      const generation = captureProtectedStateGeneration();
      setPendingVisitId(visit.visitId);
      setBusy(true);
      setFeedback(null);
      void mutationGateway(
        {
          visitId: visit.visitId,
          expectedRevision: visit.revision,
          kind,
          idempotencyKey,
          correlationId,
        },
        {
          signal: controller.signal,
          idempotencyKey: idempotencyKey as IdempotencyKey,
        },
      )
        .then(
          (outcome) => {
            if (
              controller.signal.aborted ||
              sequence.current !== requestSequence ||
              !isProtectedStateGenerationCurrent(generation)
            ) {
              return;
            }
            if (outcome.kind !== "confirmed") {
              setFeedback({
                tone: "critical",
                message:
                  outcome.kind === "conflict"
                    ? `The visit changed at revision ${outcome.currentRevision}. Refresh before trying again.`
                    : `The action was not allowed (${outcome.reason.replaceAll("_", " ").toLowerCase()}).`,
              });
              return;
            }
            setLoadState((state) =>
              state.kind === "ready" && state.projectionKey === projectionKey
                ? {
                    kind: "ready",
                    projectionKey: state.projectionKey,
                    snapshot: {
                      ...state.snapshot,
                      visits: state.snapshot.visits.map((item) =>
                        item.visitId === outcome.visit.visitId
                          ? outcome.visit
                          : item,
                      ),
                      events: [...state.snapshot.events, outcome.event],
                      generatedAt: outcome.event.occurredAt,
                    },
                  }
                : state,
            );
            setFeedback({
              tone: "positive",
              message: `${outcome.event.kind.replaceAll("_", " ").toLowerCase()} was recorded.`,
            });
          },
          () => {
            if (!controller.signal.aborted) {
              setFeedback({
                tone: "critical",
                message: "The operational action could not be confirmed.",
              });
            }
          },
        )
        .finally(() => {
          if (sequence.current === requestSequence) {
            activeRequest.current = null;
            setPendingVisitId(null);
            setBusy(false);
          }
        });
    },
    [canMutate, loadState, mutationGateway, projectionKey],
  );

  const preparePrint = useCallback(
    (visitIds: readonly string[], format: VisitPacketCodeFormat) => {
      if (
        !canPrint ||
        !packetGateway ||
        !printerProfileId ||
        !locationId ||
        activeRequest.current ||
        loadState.kind !== "ready"
      ) {
        return;
      }
      const idempotencyKey = secureId("print_intent");
      if (!idempotencyKey) return;
      const controller = new AbortController();
      activeRequest.current = controller;
      const requestSequence = sequence.current + 1;
      sequence.current = requestSequence;
      const generation = captureProtectedStateGeneration();
      const requestAuthorityKey = printAuthorityKey;
      setBusy(true);
      setFeedback(null);
      void packetGateway(
        {
          visitIds,
          date: selectedDate,
          locationId,
          printerProfileId,
          format,
        },
        {
          signal: controller.signal,
          idempotencyKey: idempotencyKey as IdempotencyKey,
        },
      )
        .then(
          (bundles) => {
            if (
              controller.signal.aborted ||
              sequence.current !== requestSequence ||
              !hasCurrentPrintAuthority(requestAuthorityKey, generation)
            ) {
              return;
            }
            setPrintBundles(bundles);
            setFeedback({
              tone: "positive",
              message: `${bundles.length} ${bundles.length === 1 ? "packet is" : "packets are"} prepared. Opening the print dialog will create the audit ${bundles.length === 1 ? "event" : "events"}.`,
            });
          },
          () => {
            if (
              !controller.signal.aborted &&
              sequence.current === requestSequence &&
              hasCurrentPrintAuthority(requestAuthorityKey, generation)
            ) {
              setFeedback({
                tone: "critical",
                message: "The visit packet print intent could not be prepared.",
              });
            }
          },
        )
        .finally(() => {
          if (sequence.current === requestSequence) {
            activeRequest.current = null;
            setBusy(false);
          }
        });
    },
    [
      canPrint,
      printAuthorityKey,
      loadState,
      locationId,
      packetGateway,
      printerProfileId,
      selectedDate,
    ],
  );

  const openPrintDialog = useCallback(() => {
    if (
      !canPrint ||
      !printDialogAuditGateway ||
      !locationId ||
      activeRequest.current ||
      loadState.kind !== "ready" ||
      printBundles.length === 0 ||
      printFrame.current !== null
    ) {
      return;
    }
    const printerProfileId = printBundles[0]?.printerProfileId;
    const format = printBundles[0]?.codeFormat;
    if (
      !printerProfileId ||
      !format ||
      printBundles.some(
        (bundle) =>
          bundle.printerProfileId !== printerProfileId ||
          bundle.codeFormat !== format,
      )
    ) {
      setFeedback({
        tone: "critical",
        message: "The print set is inconsistent. Prepare the packets again.",
      });
      return;
    }
    const idempotencyKey = secureId("print_dialog");
    const correlationId = secureId("print_correlation");
    if (!idempotencyKey || !correlationId) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    const requestSequence = sequence.current + 1;
    sequence.current = requestSequence;
    const generation = captureProtectedStateGeneration();
    const requestAuthorityKey = printAuthorityKey;
    setBusy(true);
    setFeedback(null);
    void printDialogAuditGateway(
      {
        items: printBundles.map((bundle) => ({
          packetId: bundle.packet.packetId,
          visitId: bundle.packet.visitId,
          packetRevision: bundle.packet.revision,
        })),
        date: selectedDate,
        locationId,
        printerProfileId,
        format,
        idempotencyKey,
        correlationId,
      },
      {
        signal: controller.signal,
        idempotencyKey: idempotencyKey as IdempotencyKey,
      },
    )
      .then(
        (audits) => {
          if (
            controller.signal.aborted ||
            sequence.current !== requestSequence ||
            !hasCurrentPrintAuthority(requestAuthorityKey, generation)
          ) {
            return;
          }
          const auditedBundles = printBundles.map((bundle) => {
            const audit = audits.find(
              (candidate) =>
                candidate.visitId === bundle.packet.visitId &&
                candidate.packetRevision === bundle.packet.revision &&
                candidate.printerProfileId === bundle.printerProfileId &&
                candidate.format === bundle.codeFormat &&
                candidate.eventKind === "PRINT_DIALOG_OPENED",
            );
            return audit ? { ...bundle, printAudit: audit } : null;
          });
          if (
            audits.length !== printBundles.length ||
            auditedBundles.some((bundle) => bundle === null)
          ) {
            setFeedback({
              tone: "critical",
              message:
                "The print dialog was not opened because every packet audit could not be confirmed.",
            });
            return;
          }
          const confirmedBundles = auditedBundles.filter(
            (bundle) => bundle !== null,
          );
          setPrintBundles(confirmedBundles);
          setFeedback({
            tone: "positive",
            message: `${audits.length} print-dialog ${audits.length === 1 ? "event was" : "events were"} audited.`,
          });
          printFrame.current = requestAnimationFrame(() => {
            printFrame.current = null;
            if (
              controller.signal.aborted ||
              sequence.current !== requestSequence ||
              !hasCurrentPrintAuthority(requestAuthorityKey, generation)
            ) {
              setPrintBundles([]);
              return;
            }
            try {
              window.print();
            } finally {
              setPrintBundles([]);
            }
          });
        },
        () => {
          if (
            !controller.signal.aborted &&
            sequence.current === requestSequence &&
            hasCurrentPrintAuthority(requestAuthorityKey, generation)
          ) {
            setFeedback({
              tone: "critical",
              message:
                "The print dialog was not opened because its audit could not be recorded.",
            });
          }
        },
      )
      .finally(() => {
        if (sequence.current === requestSequence) {
          activeRequest.current = null;
          setBusy(false);
        }
      });
  }, [
    canPrint,
    printAuthorityKey,
    loadState,
    locationId,
    printBundles,
    printDialogAuditGateway,
    selectedDate,
  ]);

  const manageBarcode = useCallback(
    (bundle: StaffVisitPacketBundle, kind: BarcodeGrantControlKind) => {
      if (
        !canManageCodes ||
        !barcodeControlGateway ||
        !locationId ||
        activeRequest.current ||
        loadState.kind !== "ready"
      ) {
        return;
      }
      const idempotencyKey = secureId("barcode_control");
      const correlationId = secureId("barcode_correlation");
      if (!idempotencyKey || !correlationId) return;
      const controller = new AbortController();
      activeRequest.current = controller;
      const requestSequence = sequence.current + 1;
      sequence.current = requestSequence;
      const generation = captureProtectedStateGeneration();
      const requestAuthorityKey = printAuthorityKey;
      setBusy(true);
      setFeedback(null);
      void barcodeControlGateway(
        {
          kind,
          grantId: bundle.grantId,
          visitId: bundle.packet.visitId,
          expectedRevision: bundle.grantRevision,
          idempotencyKey,
          correlationId,
          date: selectedDate,
          locationId,
          printerProfileId: bundle.printerProfileId,
          format: bundle.codeFormat,
        },
        {
          signal: controller.signal,
          idempotencyKey: idempotencyKey as IdempotencyKey,
        },
      )
        .then(
          (outcome) => {
            if (
              controller.signal.aborted ||
              sequence.current !== requestSequence ||
              !hasCurrentPrintAuthority(requestAuthorityKey, generation)
            ) {
              return;
            }
            if (outcome.kind !== "confirmed") {
              setFeedback({
                tone: "critical",
                message:
                  outcome.kind === "conflict"
                    ? `The code changed at revision ${outcome.currentRevision}. Prepare the packet again.`
                    : "The code action was not allowed.",
              });
              return;
            }
            setPrintBundles((current) =>
              current.flatMap((item) =>
                item.grantId !== outcome.revokedGrantId
                  ? [item]
                  : outcome.replacement
                    ? [outcome.replacement]
                    : [],
              ),
            );
            setFeedback({
              tone: "positive",
              message:
                outcome.replacement === null
                  ? "The visit code was revoked and removed from the print set."
                  : "The prior code was revoked and a replacement packet was prepared with a new print intent.",
            });
          },
          () => {
            if (
              !controller.signal.aborted &&
              sequence.current === requestSequence &&
              hasCurrentPrintAuthority(requestAuthorityKey, generation)
            ) {
              setFeedback({
                tone: "critical",
                message: "The code action could not be confirmed.",
              });
            }
          },
        )
        .finally(() => {
          if (sequence.current === requestSequence) {
            activeRequest.current = null;
            setBusy(false);
          }
        });
    },
    [
      barcodeControlGateway,
      canManageCodes,
      printAuthorityKey,
      loadState,
      locationId,
      selectedDate,
    ],
  );

  const currentLoadState: LoadState =
    loadState.projectionKey === projectionKey
      ? loadState
      : { kind: "loading", projectionKey };

  if (!authorized) {
    return (
      <ContentState kind="warning" title="Staff operations unavailable">
        An assigned-location staff permission is required.
      </ContentState>
    );
  }
  if (!readGateway) {
    return (
      <ContentState kind="warning" title="Operations connection unavailable">
        The production staff-operations gateway remains fail-closed. No local
        data is substituted.
      </ContentState>
    );
  }
  if (currentLoadState.kind === "loading") {
    return <ContentState kind="loading" title="Loading assigned visits" />;
  }
  if (currentLoadState.kind === "error") {
    return (
      <ContentState
        kind="error"
        title="Assigned visits could not be loaded"
        actions={
          <button type="button" onClick={load}>
            Retry
          </button>
        }
      />
    );
  }
  return (
    <StaffOperationsView
      view={view}
      snapshot={currentLoadState.snapshot}
      canMutate={canMutate}
      canPrint={canPrint}
      canManageCodes={canManageCodes}
      syntheticPreview={syntheticPreview}
      assignedLocationIds={subject?.locationIds ?? []}
      selectedLocationId={locationId}
      selectedDate={selectedDate}
      selectedAppointmentId={appointmentId}
      pendingVisitId={pendingVisitId}
      busy={busy}
      feedback={feedback}
      printBundles={printBundles}
      onTransition={transition}
      onPrint={preparePrint}
      onOpenPrintDialog={openPrintDialog}
      onBarcodeControl={manageBarcode}
      onFilterChange={(next) => {
        setLocationId(next.locationId);
        setSelectedDate(next.date);
        setAppointmentId(next.appointmentId as AppointmentId | null);
        setVisitId(null);
        setPrintBundles([]);
        setFeedback(null);
      }}
    />
  );
}
